import { useState, useEffect } from "react";
import api, { formatApiError } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";

function ChangePasswordCard() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      return toast.error("New passwords do not match");
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password changed successfully. Use it on your next login.");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-black/10 p-6 md:p-8 mt-6" data-testid="change-password-form">
      <p className="small-caps text-gold mb-2">Change Admin Password</p>
      <p className="text-xs text-muted-foreground mb-6">Your password is stored securely (bcrypt-hashed). Only you can change it while logged in.</p>
      <div className="grid md:grid-cols-3 gap-6">
        <div>
          <Label className="text-xs uppercase tracking-widest">Current Password</Label>
          <Input required type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className="mt-2" data-testid="current-password" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">New Password (min 6 chars)</Label>
          <Input required type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="mt-2" data-testid="new-password" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest">Confirm New Password</Label>
          <Input required type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="mt-2" data-testid="confirm-password" />
        </div>
      </div>
      <button type="submit" disabled={saving} className="mt-6 bg-[#111] text-[#FAF9F6] px-10 py-4 text-xs uppercase tracking-widest hover:bg-gold hover:text-[#111] transition-colors duration-300 disabled:opacity-50" data-testid="change-password-submit">
        {saving ? "Updating..." : "Change Password"}
      </button>
    </form>
  );
}

const FIELDS = [
  { key: "business_name", label: "Business Name" },
  { key: "phone", label: "Phone Number" },
  { key: "whatsapp", label: "WhatsApp Number" },
  { key: "email", label: "Public Email Address" },
  { key: "notification_email", label: "Order Notification Email (where you receive alerts)" },
  { key: "address", label: "Business Address" },
  { key: "business_state", label: "Business State (for GST invoices)" },
  { key: "gstin", label: "GSTIN" },
  { key: "hsn_code", label: "HSN Code (e.g. 8306)" },
  { key: "gst_rate", label: "GST Rate % (e.g. 18)" },
  { key: "instagram", label: "Instagram URL" },
  { key: "facebook", label: "Facebook URL" },
  { key: "announcement", label: "Top Announcement Bar Text" },
  { key: "announcement_link", label: "Announcement Bar Link (optional, e.g. /shop)" },
  { key: "hero_title", label: "Homepage Hero Title" },
  { key: "hero_subtitle", label: "Homepage Hero Subtitle" },
  { key: "shipping_message", label: "Shipping Message (shown at checkout)" },
  { key: "estimated_delivery", label: "Estimated Delivery Text" },
];

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", settings);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="h-40 bg-muted animate-pulse" />;

  return (
    <div data-testid="admin-settings">
      <h1 className="font-serif text-3xl font-semibold mb-2">Settings</h1>
      <p className="text-sm text-muted-foreground mb-8">Update your business details, contact info and website text. Changes go live instantly.</p>
      <form onSubmit={save} className="bg-white border border-black/10 p-6 md:p-8 grid md:grid-cols-2 gap-6">
        {FIELDS.map((f) => (
          <div key={f.key} className={["hero_subtitle", "shipping_message", "address"].includes(f.key) ? "md:col-span-2" : ""}>
            <Label className="text-xs uppercase tracking-widest">{f.label}</Label>
            <Input value={settings[f.key] || ""} onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })} className="mt-2" data-testid={`setting-${f.key}`} />
          </div>
        ))}
        <div className="md:col-span-2">
          <button type="submit" disabled={saving} className="bg-[#111] text-[#FAF9F6] px-10 py-4 text-xs uppercase tracking-widest hover:bg-gold hover:text-[#111] transition-colors duration-300 disabled:opacity-50" data-testid="settings-save">
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </form>
      <ChangePasswordCard />
    </div>
  );
}
